import { makePersisted } from "@solid-primitives/storage";
import { TbHistory, TbX } from "solid-icons/tb";
import { type Component, createEffect, createMemo, createSignal, onCleanup, Show } from "solid-js";
import { isServer } from "solid-js/web";
import { useI18n } from "~/i18n/context";
import { useWire } from "~/wire";
import { ActionRunsList } from "./internal/ActionRunsList";

const STORAGE_KEY = "bsky:actionRunsDrawer:open";

// Module-level so the persisted signal is shared across re-mounts and survives route transitions.
const [isOpen, setIsOpen] = makePersisted(createSignal<boolean>(false), {
  storage: typeof window !== "undefined" ? localStorage : undefined,
  name: STORAGE_KEY,
});

/**
 * Right-anchored overlay that lists the current action's recent runs so users
 * can navigate between them without leaving the chat thread view. Only renders
 * when the navigation service reports an execution context.
 *
 * Mounted at AppLayout level outside the main scroll wrapper so the trigger
 * and drawer remain pinned regardless of chat scroll position.
 */
export const ActionRunsDrawer: Component = () => {
  const wire = useWire();
  const { t } = useI18n();
  const layout = wire.ui.layout;
  const nav = wire.navigation;

  const ctx = createMemo(() => nav.executionContext());
  const activeChatId = createMemo(() => nav.state().primaryId ?? undefined);

  // When the global SidePanel takes a column on large screens, hide both the trigger and the drawer
  // so the chat content isn't squeezed between two right-side panels. The persisted open state is
  // preserved across the hide/show cycle, so closing the side panel restores the drawer's state.
  const sideHidden = () => layout.sideCollapsed() || layout.sideMinimized() || nav.isSideHidden();
  const sidePanelTakesColumn = () => !!nav.state().sideId && !sideHidden() && layout.isLargeScreen();

  // Leave room for the existing SidePanelToggleButton (positioned at right-2.75 in AppLayout).
  const TRIGGER_TOGGLE_GAP_PX = 44;

  const toggle = () => setIsOpen((v) => !v);
  const close = () => setIsOpen(false);

  let triggerRef: HTMLButtonElement | undefined;
  let asideRef: HTMLElement | undefined;

  // Only treat the drawer as dismissable while it is actually visible. When the global side panel
  // takes a column the drawer is hidden but its open state is intentionally preserved (restored when
  // the side panel closes), so an unrelated click must not flip it closed in that state.
  const isDrawerActive = () => isOpen() && !!ctx() && !sidePanelTakesColumn();

  createEffect(() => {
    if (isServer || !isDrawerActive()) return;

    const onPointerDown = (event: PointerEvent) => {
      const target = event.target as Node | null;
      if (!target) return;
      // The trigger toggles the drawer itself; excluding it lets its own onClick close once
      // without this listener also closing (which would double-toggle back open).
      if (asideRef?.contains(target) || triggerRef?.contains(target)) return;
      close();
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      close();
      triggerRef?.focus();
    };

    // Capture phase + listening on pointerdown (not click) means the close runs before the
    // press resolves and never swallows it, so the underlying element still receives the click.
    document.addEventListener("pointerdown", onPointerDown, true);
    document.addEventListener("keydown", onKeyDown);

    onCleanup(() => {
      document.removeEventListener("pointerdown", onPointerDown, true);
      document.removeEventListener("keydown", onKeyDown);
    });
  });

  return (
    <Show when={ctx()}>
      {(execCtx) => (
        <Show when={!sidePanelTakesColumn()}>
          {/* Trigger pill — pinned to the top-right of the main content area. z-50 so the drawer's
              close X is not covered by the SidePanelToggleButton (which is also z-50 and earlier in DOM). */}
          <button
            ref={triggerRef}
            type="button"
            onClick={toggle}
            aria-expanded={isOpen()}
            aria-label={isOpen() ? t("actions.run_history_drawer_close") : t("actions.run_history_drawer_open")}
            class="absolute top-3 z-50 inline-flex items-center gap-1.5 rounded-full border border-primary-border bg-panel px-2.5 py-1 text-xs font-medium text-primary-text shadow-sm transition-colors hover:bg-tile-hover"
            style={{ right: `${TRIGGER_TOGGLE_GAP_PX}px` }}
          >
            <TbHistory class="size-3.5" />
            <span>{t("actions.run_history_drawer_title")}</span>
          </button>

          {/* Mobile/non-large backdrop. */}
          <Show when={isOpen() && !layout.isLargeScreen()}>
            <div role="presentation" class="absolute inset-0 z-40 bg-black/10" onClick={close} />
          </Show>

          {/* Drawer panel */}
          <aside
            ref={asideRef}
            class="absolute inset-y-0 right-0 z-50 flex flex-col border-l border-primary-border bg-panel shadow-xl transition-transform duration-200 ease-out"
            classList={{
              "translate-x-0": isOpen(),
              "translate-x-full pointer-events-none": !isOpen(),
            }}
            style={{ width: layout.isLargeScreen() ? "320px" : "min(100%, 360px)" }}
            aria-hidden={!isOpen()}
          >
            <div class="flex h-10 shrink-0 items-center justify-between border-b border-primary-border px-3">
              <h2 class="text-sm font-medium text-primary-text">{t("actions.run_history_drawer_title")}</h2>
              <button
                type="button"
                onClick={close}
                aria-label={t("actions.run_history_drawer_close")}
                class="rounded p-1 text-secondary-text transition-colors hover:bg-tile-hover hover:text-primary-text"
              >
                <TbX class="size-4" />
              </button>
            </div>
            <div class="flex-1 overflow-y-auto p-3">
              <ActionRunsList actionId={execCtx().actionId} activeChatId={activeChatId()} onRunSelected={close} />
            </div>
          </aside>
        </Show>
      )}
    </Show>
  );
};
